import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Composant SEO centralisé pour Dalal.
 *
 * Gère :
 *   - <title> et meta description
 *   - canonical (sans dépendre de window pour être pré-rendu-friendly)
 *   - Open Graph + Twitter Card
 *   - JSON-LD structured data (passé en prop)
 *   - noindex pour les pages privées
 *
 * Usage minimal :
 *   <Seo title="Devenir partenaire" description="…" path="/partner" />
 *
 * Avec données structurées :
 *   <Seo
 *     title="Studio meublé à Dakar"
 *     description="…"
 *     path={`/details/${code}`}
 *     image={place.medias?.[0]?.url}
 *     jsonLd={{
 *       "@context": "https://schema.org",
 *       "@type": "LodgingBusiness",
 *       …
 *     }}
 *   />
 */

const SITE_URL = "https://dalal.sn";
const DEFAULT_IMAGE = `${SITE_URL}/dalalicon512.png`;
const DEFAULT_TITLE =
  "Dalal – Réservation de chambres et appartements à Dakar et partout au Sénégal";
const DEFAULT_DESCRIPTION =
  "Trouvez et réservez en ligne des chambres, appartements et suites à Dakar et partout au Sénégal. Locations courte et longue durée à Dakar, Saly, Mbour, Saint-Louis et partout au pays.";

const buildCanonical = (path) => {
  if (!path) return SITE_URL + "/";
  // On retire les query params et fragments du canonical : Google les déteste pour la canonicalisation.
  const clean = path.split("?")[0].split("#")[0];
  return clean.startsWith("http")
    ? clean
    : `${SITE_URL}${clean.startsWith("/") ? clean : `/${clean}`}`;
};

const Seo = ({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  children,
}) => {
  const fullTitle = title
    ? title.toLowerCase().includes("dalal")
      ? title
      : `${title} | Dalal`
    : DEFAULT_TITLE;
  const finalDescription = (description || DEFAULT_DESCRIPTION).slice(0, 300);
  const canonical = buildCanonical(path);
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Dalal" />
      <meta property="og:locale" content="fr_SN" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}

      {children}
    </Helmet>
  );
};

export default Seo;
